import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { reportsAPI } from '../../services/api';

const QuestionView = ({ startDate, endDate, campus, gradeLevel }) => {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    fetchPopularActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, JSON.stringify(campus), JSON.stringify(gradeLevel)]);

  const fetchPopularActivities = async () => {
    try {
      setLoading(true);
      const response = await reportsAPI.getPopularActivities({ startDate, endDate, campus, gradeLevel });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching popular activities:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Most Popular Activities</h3>
        <p className="text-sm text-gray-600">
          Activities selected most frequently by students across all survey questions
        </p>
      </div>

      {/* Chart */}
      {activities.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <ResponsiveContainer width="100%" height={Math.max(400, activities.length * 30)}>
            <BarChart data={activities} layout="vertical" margin={{ left: 20, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" label={{ value: 'Number of Selections', position: 'insideBottom', offset: -5 }} />
              <YAxis
                type="category"
                dataKey="activity"
                width={300}
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.length > 50 ? `${value.substring(0, 50)}...` : value}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="bg-white p-3 border border-gray-200 rounded shadow-lg max-w-md">
                        <p className="text-sm font-medium text-gray-900 mb-1">{payload[0].payload.activity}</p>
                        <p className="text-sm text-gray-600">
                          Selected by <span className="font-semibold text-purple-600">{payload[0].value}</span> students
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="count" fill="#7c3aed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* List View */}
      {activities.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Activity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selections
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {activities.map((activity, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{index + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {activity.activity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {activity.count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activities.length === 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <p className="text-gray-500">No activity data available for the selected time period</p>
        </div>
      )}
    </div>
  );
};

export default QuestionView;
